/**
 * @module FilialenService
 * @remarks RTK Query service for Filialen (branch) data
 */

import { Filiale, FilialenResponseItem } from "@models/filialien.model";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";


/**
 * API service for managing Filialen (branch) data
 */
export const FilialenApi = createApi({
  reducerPath: "filialenApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Filiale"],
  endpoints: (builder) => ({
    getFilialen: builder.query<Filiale[], void>({
      query: () => ({
        url: "data/filialen",
        method: "POST",
        body: {
          where: {
            status: 1
          },
          choiceAsString: "1",
          fields: [
            "id", "status", "filialnummer", "title_filiale", "marke", "plz", "ort", "bundesland", "strasse_hausnummer", "telefon", "users"
          ]
        },
      }),
      // Transformiere die verschachtelte Antwort in ein einfaches Array von Filialen
      transformResponse: (response: FilialenResponseItem[]) => {
        // Flache Liste aller Filialen aus allen Einträgen extrahieren
        const allFilialen: Filiale[] = [];
        
        if (Array.isArray(response)) {
          response.forEach(item => {
            if (item.filialen && Array.isArray(item.filialen)) {
              allFilialen.push(...item.filialen);
            }
          });
        }
        
        return allFilialen;
      },
      providesTags: ["Filiale"],
      keepUnusedDataFor: 86400, // Cache für 24 Stunden
    }),
    getFiliale: builder.query<Filiale, string>({
      query: (filialeId) => ({
        url: "data/filialen",
        method: "POST",
        body: {
          where: {
            id: filialeId,
            status: 1
          },
          choiceAsString: "1",
          fields: [
            "id", "status", "filialnummer", "title_filiale", "marke", "plz", "ort", "bundesland", "strasse_hausnummer", "telefon", "users"
          ]
        },
      }),
      transformResponse: (response: FilialenResponseItem[]) => {
        // Extrahiere die erste Filiale aus der ersten Antwort
        if (!response || !Array.isArray(response) || response.length === 0 || 
            !response[0].filialen || response[0].filialen.length === 0) {
          throw new Error("Filiale nicht gefunden");
        }
        
        return response[0].filialen[0];
      },
      providesTags: (result, error, id) => [{ type: "Filiale", id }],
    }),
    invalidateFilialen: builder.mutation<void, void>({
      queryFn: () => ({ data: undefined }),
      invalidatesTags: ["Filiale"],
    }),
  }),
});

// Export hooks für Verwendung in Komponenten
export const {
  useGetFilialenQuery,
  useGetFilialeQuery,
  useInvalidateFilialenMutation,
} = FilialenApi;